import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { IconSocial } from '../assets/icons'
import { headerLinks, followLinks } from '../config'
import { useOnClickOutside } from '../hooks'

const StyledHamburger = styled.button`
  display: none;
  .ham-box {
    display: inline-block;
    position: relative;
    width: var(--hamburger-width);
    height: 24px;
  }
  .ham-box-inner {
    position: absolute;
    top: 50%;
    right: 0;
    width: var(--hamburger-width);
    height: 2px;
    border-radius: var(--border-radius);
    background-color: var(--white);
    transition-duration: 0.22s;
    transition-property: transform;
    transition-delay: ${props => (props.menuOpen ? `0.12s` : `0s`)};
    transform: rotate(${props => (props.menuOpen ? `225deg` : `0deg`)});
    transition-timing-function: cubic-bezier(${props => (props.menuOpen ? `0.215, 0.61, 0.355, 1` : `0.55, 0.055, 0.675, 0.19`)});
    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      left: auto;
      right: 0;
      width: var(--hamburger-width);
      height: 2px;
      border-radius: 4px;
      background-color: var(--white);
      transition-timing-function: ease;
      transition-duration: 0.15s;
      transition-property: transform;
    }
    &:before {
      width: ${props => (props.menuOpen ? `100%` : `100%`)};
      top: ${props => (props.menuOpen ? `0` : `-10px`)};
      opacity: ${props => (props.menuOpen ? 0 : 1)};
      transition: ${({ menuOpen }) => (menuOpen ? 'var(--ham-before-active)' : 'var(--ham-before)')};
    }
    &:after {
      width: ${props => (props.menuOpen ? `100%` : `100%`)};
      bottom: ${props => (props.menuOpen ? `0` : `-10px`)};
      transform: rotate(${props => (props.menuOpen ? `-90deg` : `0`)});
      transition: ${({ menuOpen }) => (menuOpen ? 'var(--ham-after-active)' : 'var(--ham-after)')};
    }
  }
  @media screen and (max-width: 1023px) {
    display: flex;
    cursor: pointer;
    position: relative;
    z-index: 10;
    margin-right: -15px;
    padding: 15px;
    border: 0;
    border-radius: 50%;
    color: inherit;
    text-transform: none;
    transition-timing-function: linear;
    transition-duration: 0.15s;
    transition-property: opacity, filter;
    background-color: transparent;
  }
`
const StyledSidebar = styled.aside`
  display: none;
  position: fixed;
  z-index: 9;
  top: 0;
  right: 0;
  bottom: 0;
  padding: 90px 25px 0px 25px;
  width: min(75vw, 400px);
  height: 100vh;
  outline: 0;
  background-color: var(--black-medium);
  box-shadow: -10px 0px 30px -15px var(--black);
  transform: translateX(${props => (props.menuOpen ? 0 : 400)}px);
  visibility: ${props => (props.menuOpen ? 'visible' : 'hidden')};
  transition: var(--transition);
  @media screen and (max-width: 1023px) {
    display: block;
  }
`
const MenuNavigation = styled.nav`
  display: flex;
  width: 100%;
  flex-direction: column;
  text-align: left;
  .nav-link {
    display: inline-block;
    cursor: pointer;
    padding: 10px 20px 13px;
    margin-bottom: 5px;
    color: var(--white);
    font-family: var(--font-condensed);
    font-size: var(--fz-lg);
    font-weight: 500;
    line-height: 1;
    border-radius: 4px;
    transition: var(--transition);
    background-color: transparent;
    &:hover {
      color: var(--white);
      background-color: var(--grey-hover);
    }
  }
`
const MenuSocials = styled.div`
  position: absolute;
  left: 46px;
  bottom: 80px;
  display: flex;
  width: calc(100% - 92px);
  flex-direction: column;
  text-align: left;
  .button {
    display: inline-block;
    cursor: pointer;
    width: 100%;
    margin: 60px 0 40px 0;
    padding: 14px 0 17px;
    text-align: center;
    color: var(--white);
    font-family: var(--font-condensed);
    font-size: var(--fz-lg);
    font-weight: 500;
    line-height: 1;
    border-radius: 4px;
    transition: var(--transition);
    background-color: var(--secondary);
    box-shadow: 0 0 20px 0 rgb(0 0 0 / 10%);
    &:hover {
      color: var(--white);
      background-color: var(--grey-hover);
    }
  }
  .social-links {
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;
    a {
      display: block;
      width: 44px;
      height: 44px;
      padding: 10px;
      margin: 0 4px;
      border-radius: 50%;
      overflow: hidden;
      background-color: var(--black-light);
      svg {
        width: 24px;
        height: 24px;
      }
      &:hover {
        color: var(--white);
        background-color: var(--grey-hover);
      }
    }
  }
`

const Menu = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const toggleMenu = () => setMenuOpen(!menuOpen)

  const wrapperRef = useRef(null)
  const buttonRef = useRef(null)

  const navigate = useNavigate()
  const handleClick = target => {
    setMenuOpen(false)
    const timer = setTimeout(function () {
      navigate(target)
    }, 200)
  }

  useOnClickOutside(wrapperRef, () => setMenuOpen(false))

  return (
    <>
      <Helmet>
        <body className={menuOpen ? 'blur' : ''} />
      </Helmet>
      <div ref={wrapperRef}>
        <StyledHamburger onClick={toggleMenu} menuOpen={menuOpen} ref={buttonRef} aria-label="Menu">
          <div className="ham-box">
            <div className="ham-box-inner" />
          </div>
        </StyledHamburger>
        <StyledSidebar menuOpen={menuOpen} aria-hidden={!menuOpen} tabIndex={menuOpen ? 1 : -1}>
          <MenuNavigation>
            {headerLinks &&
              headerLinks.map(({ url, name }, i) => (
                <div className="nav-link" key={i} onClick={() => handleClick(url)}>
                  {name}
                </div>
              ))}
          </MenuNavigation>
          <MenuSocials>
            <div className="button" onClick={() => handleClick('/get-a-free-quote')}>
              Get a free quote
            </div>
            <div className="social-links">
              {followLinks &&
                followLinks.map(({ name, url }, i) => (
                  <a href={url} key={i} target="_blank" aria-label={name}>
                    <IconSocial name={name} />
                  </a>
                ))}
            </div>
          </MenuSocials>
        </StyledSidebar>
      </div>
    </>
  )
}

export default Menu
